// src/components/charts/FlowLoopChart.jsx
import React, { memo, useMemo, useState } from "react";
import { Line } from "react-chartjs-2";
import { Chart as ChartJS, registerables } from "chart.js";
import { getPrimaryColour } from "../../utils/getPrimaryColour";
import { Switch } from "../Switch";

const zeroAxesPlugin = {
  id: "resultZeroAxesPlugin",
  afterDraw(chart, args, opts) {
    if (!opts || opts.enabled !== true) return;

    const { ctx, chartArea, scales } = chart;
    const x = scales.x;
    const y = scales.y;
    if (!x || !y) return;

    const x0 = x.getPixelForValue(0);
    const y0 = y.getPixelForValue(0);

    const drawX0 = opts.drawX0 !== false;
    const drawY0 = opts.drawY0 !== false;

    ctx.save();
    ctx.strokeStyle = opts.color || getPrimaryColour();
    ctx.globalAlpha = opts.alpha ?? 0.25;
    ctx.lineWidth = opts.lineWidth ?? 1;

    if (drawX0 && x0 >= chartArea.left && x0 <= chartArea.right) {
      ctx.beginPath();
      ctx.moveTo(x0, chartArea.top);
      ctx.lineTo(x0, chartArea.bottom);
      ctx.stroke();
    }

    if (drawY0 && y0 >= chartArea.top && y0 <= chartArea.bottom) {
      ctx.beginPath();
      ctx.moveTo(chartArea.left, y0);
      ctx.lineTo(chartArea.right, y0);
      ctx.stroke();
    }

    ctx.restore();
  },
};

const peakLinesPlugin = {
  id: "resultPeakLinesPlugin",
  afterDatasetsDraw(chart, args, opts) {
    const { ctx, chartArea, scales } = chart;
    const x = scales.x;
    const y = scales.y;
    if (!x || !y) return;

    const peaks = opts?.peaks;
    if (!peaks?.hasPeaks) return;

    const y0 = y.getPixelForValue(0);
    const xEx = x.getPixelForValue(peaks.vExPeak);
    const xIn = x.getPixelForValue(peaks.vInPeak);
    const yEx = y.getPixelForValue(peaks.fExPeak);
    const yIn = y.getPixelForValue(peaks.fInPeak);

    ctx.save();
    ctx.strokeStyle = opts?.color || getPrimaryColour();
    ctx.globalAlpha = opts?.alpha ?? 1;
    ctx.setLineDash([6, 4]);
    ctx.lineWidth = opts?.lineWidth ?? 1.5;

    if (
      Number.isFinite(xEx) &&
      xEx >= chartArea.left &&
      xEx <= chartArea.right
    ) {
      ctx.beginPath();
      ctx.moveTo(xEx, y0);
      ctx.lineTo(xEx, yEx);
      ctx.stroke();
    }

    if (
      Number.isFinite(xIn) &&
      xIn >= chartArea.left &&
      xIn <= chartArea.right
    ) {
      ctx.beginPath();
      ctx.moveTo(xIn, y0);
      ctx.lineTo(xIn, yIn);
      ctx.stroke();
    }

    ctx.restore();
  },
};

const quadrantLabelsPlugin = {
  id: "resultQuadrantLabelsPlugin",
  afterDraw(chart, args, opts) {
    if (!opts || opts.enabled !== true) return;

    const { ctx, chartArea } = chart;

    ctx.save();
    ctx.fillStyle = opts?.color || getPrimaryColour();
    ctx.globalAlpha = opts?.alpha ?? 1;
    ctx.font = "700 18px sans-serif";

    ctx.textBaseline = "top";
    ctx.textAlign = "left";
    ctx.fillText("A", chartArea.left + 8, chartArea.top + 6);

    ctx.textAlign = "right";
    ctx.fillText("B", chartArea.right - 8, chartArea.top + 6);

    ctx.textBaseline = "bottom";
    ctx.textAlign = "left";
    ctx.fillText("C", chartArea.left + 8, chartArea.bottom - 6);

    ctx.textAlign = "right";
    ctx.fillText("D", chartArea.right - 8, chartArea.bottom - 6);

    ctx.restore();
  },
};

const shadingPlugin = {
  id: "resultShadingPlugin",
  beforeDatasetsDraw(chart, args, opts) {
    if (!opts?.enabled) return;

    const { ctx, scales } = chart;
    const x = scales.x;
    const y = scales.y;
    if (!x || !y) return;

    const peaks = opts?.peaks;
    const points = Array.isArray(opts?.points) ? opts.points : [];

    if (!peaks?.hasPeaks || points.length < 2) return;

    const y0 = y.getPixelForValue(0);

    function splitWithBoundary(sortedPts, splitX) {
      if (!Array.isArray(sortedPts) || sortedPts.length < 2) {
        return { left: [], right: [] };
      }

      if (!Number.isFinite(splitX)) {
        return { left: sortedPts.slice(), right: [] };
      }

      const minX = sortedPts[0].x;
      const maxX = sortedPts[sortedPts.length - 1].x;

      if (splitX <= minX) {
        return { left: [sortedPts[0]], right: sortedPts.slice() };
      }

      if (splitX >= maxX) {
        return {
          left: sortedPts.slice(),
          right: [sortedPts[sortedPts.length - 1]],
        };
      }

      let i = -1;

      for (let k = 0; k < sortedPts.length - 1; k += 1) {
        const a = sortedPts[k];
        const b = sortedPts[k + 1];

        if (a.x <= splitX && splitX <= b.x) {
          i = k;
          break;
        }
      }

      if (i === -1) {
        return {
          left: sortedPts.filter((p) => p.x <= splitX),
          right: sortedPts.filter((p) => p.x >= splitX),
        };
      }

      const a = sortedPts[i];
      const b = sortedPts[i + 1];

      let boundary;

      if (a.x === splitX) {
        boundary = { x: a.x, y: a.y };
      } else if (b.x === splitX) {
        boundary = { x: b.x, y: b.y };
      } else {
        const t = (splitX - a.x) / (b.x - a.x || 1);
        boundary = {
          x: splitX,
          y: a.y + t * (b.y - a.y),
        };
      }

      const left = sortedPts.slice(0, i + 1);
      const right = sortedPts.slice(i + 1);

      if (left.length === 0 || left[left.length - 1].x !== boundary.x) {
        left.push(boundary);
      } else {
        left[left.length - 1] = boundary;
      }

      if (right.length === 0 || right[0].x !== boundary.x) {
        right.unshift(boundary);
      } else {
        right[0] = boundary;
      }

      return { left, right };
    }

    function drawFill(pts, fillStyle, alpha) {
      if (!pts || pts.length < 2) return;

      ctx.save();
      ctx.fillStyle = fillStyle;
      ctx.globalAlpha = alpha;

      ctx.beginPath();

      const p0 = pts[0];
      ctx.moveTo(x.getPixelForValue(p0.x), y.getPixelForValue(p0.y));

      for (let i = 1; i < pts.length; i += 1) {
        const p = pts[i];
        ctx.lineTo(x.getPixelForValue(p.x), y.getPixelForValue(p.y));
      }

      const last = pts[pts.length - 1];
      ctx.lineTo(x.getPixelForValue(last.x), y0);
      ctx.lineTo(x.getPixelForValue(p0.x), y0);
      ctx.closePath();
      ctx.fill();

      ctx.restore();
    }

    const exhale = points
      .filter((p) => p.y > 0 && Number.isFinite(p.x) && Number.isFinite(p.y))
      .slice()
      .sort((a, b) => a.x - b.x);

    const inhale = points
      .filter((p) => p.y < 0 && Number.isFinite(p.x) && Number.isFinite(p.y))
      .slice()
      .sort((a, b) => a.x - b.x);

    if (exhale.length < 2 || inhale.length < 2) return;

    const colors = opts?.colors || {};
    const alpha = opts?.alpha ?? 0.12;

    const { left: exLeft, right: exRight } = splitWithBoundary(
      exhale,
      peaks.vExPeak,
    );

    drawFill(exLeft, colors.exLeft || "#f8a0d6", alpha);
    drawFill(exRight, colors.exRight || "#a56498", alpha);

    const { left: inLeft, right: inRight } = splitWithBoundary(
      inhale,
      peaks.vInPeak,
    );

    drawFill(inRight, colors.inRight || "#744267", alpha);
    drawFill(inLeft, colors.inLeft || "#553150", alpha);
  },
};

ChartJS.register(
  ...registerables,
  zeroAxesPlugin,
  peakLinesPlugin,
  quadrantLabelsPlugin,
  shadingPlugin,
);

function cleanPoints(points = []) {
  if (!Array.isArray(points)) return [];

  return points.filter(
    (p) =>
      typeof p?.x === "number" &&
      Number.isFinite(p.x) &&
      typeof p?.y === "number" &&
      Number.isFinite(p.y),
  );
}

function computeAxisBounds(points, paddingRatio = 0.08) {
  if (!points.length) {
    return {
      xMin: -1,
      xMax: 1,
      yMin: -1,
      yMax: 1,
    };
  }

  const xs = points.map((p) => p.x);
  const ys = points.map((p) => p.y);

  const xMinRaw = Math.min(...xs);
  const xMaxRaw = Math.max(...xs);
  const yMinRaw = Math.min(...ys);
  const yMaxRaw = Math.max(...ys);

  const xRange = xMaxRaw - xMinRaw || 1;
  const yRange = yMaxRaw - yMinRaw || 1;

  return {
    xMin: xMinRaw - xRange * paddingRatio,
    xMax: xMaxRaw + xRange * paddingRatio,
    yMin: yMinRaw - yRange * paddingRatio,
    yMax: yMaxRaw + yRange * paddingRatio,
  };
}

function computeLoopPeaks(points) {
  let fExPeak = -Infinity;
  let vExPeak = null;
  let fInPeak = Infinity;
  let vInPeak = null;

  for (const p of points || []) {
    if (!Number.isFinite(p?.x) || !Number.isFinite(p?.y)) continue;

    if (p.y > 0 && p.y > fExPeak) {
      fExPeak = p.y;
      vExPeak = p.x;
    }

    if (p.y < 0 && p.y < fInPeak) {
      fInPeak = p.y;
      vInPeak = p.x;
    }
  }

  const hasPeaks =
    Number.isFinite(vExPeak) &&
    Number.isFinite(fExPeak) &&
    Number.isFinite(vInPeak) &&
    Number.isFinite(fInPeak);

  return {
    hasPeaks,
    vExPeak,
    fExPeak,
    vInPeak,
    fInPeak,
  };
}

function fmtNumber(value, dp = 4) {
  const n = Number(value);
  return Number.isFinite(n) ? n.toFixed(dp) : "--";
}

function niceStep(range, targetTicks = 7) {
  if (!Number.isFinite(range) || range <= 0) return 1;

  const raw = range / targetTicks;
  const pow10 = Math.pow(10, Math.floor(Math.log10(raw)));
  const norm = raw / pow10;

  if (norm <= 1) return 1 * pow10;
  if (norm <= 2) return 2 * pow10;
  if (norm <= 5) return 5 * pow10;
  return 10 * pow10;
}

function roundTick(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";

  if (Math.abs(n) >= 1) return n.toFixed(0);
  if (Math.abs(n) >= 0.01) return n.toFixed(2);
  return n.toFixed(3);
}

function FlowLoopChartImpl({
  title = "Flow loop",
  subtitle,
  points = [],
  height = 280,
  xTitle = "Volume proxy",
  yTitle = "Flow proxy",
  showControls = true,
  showQuadrants = true,
  showPeakLines = true,
  initialFill = false,
}) {
  const primary = getPrimaryColour();
  const [showFill, setShowFill] = useState(initialFill);

  const clean = useMemo(() => cleanPoints(points), [points]);
  const axisBounds = useMemo(() => computeAxisBounds(clean), [clean]);
  const peaks = useMemo(() => computeLoopPeaks(clean), [clean]);

  const data = useMemo(
    () => ({
      datasets: [
        {
          label: title,
          data: clean,
          parsing: false,
          pointRadius: 0,
          borderWidth: 2.75,
          tension: 0.2,
          borderColor: primary,
          backgroundColor: primary,
        },
      ],
    }),
    [clean, primary, title],
  );

  const options = useMemo(() => {
    const xStep = niceStep(axisBounds.xMax - axisBounds.xMin, 7);
    const yStep = niceStep(axisBounds.yMax - axisBounds.yMin, 7);

    return {
      responsive: true,
      maintainAspectRatio: false,
      animation: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const x = ctx.raw?.x;
              const y = ctx.raw?.y;
              return `V: ${fmtNumber(x)} | F: ${fmtNumber(y)}`;
            },
          },
        },
        resultZeroAxesPlugin: {
          enabled: true,
          color: primary,
          alpha: 0.18,
          lineWidth: 1.2,
          drawX0: true,
          drawY0: true,
        },
        resultPeakLinesPlugin: {
          color: primary,
          alpha: 0.9,
          lineWidth: 1.4,
          peaks: showPeakLines ? peaks : null,
        },
        resultQuadrantLabelsPlugin: {
          enabled: showQuadrants,
          color: primary,
          alpha: 0.9,
        },
        resultShadingPlugin: {
          enabled: showFill,
          peaks,
          points: clean,
          alpha: 0.12,
          colors: {
            exLeft: "#f8a0d6",
            exRight: "#a56498",
            inLeft: "#553150",
            inRight: "#744267",
          },
        },
      },
      scales: {
        x: {
          type: "linear",
          min: axisBounds.xMin,
          max: axisBounds.xMax,
          title: {
            display: true,
            text: xTitle,
          },
          ticks: {
            stepSize: xStep,
            includeBounds: true,
            autoSkip: true,
            maxRotation: 0,
            minRotation: 0,
            callback: roundTick,
          },
        },
        y: {
          type: "linear",
          min: axisBounds.yMin,
          max: axisBounds.yMax,
          title: {
            display: true,
            text: yTitle,
          },
          ticks: {
            stepSize: yStep,
            includeBounds: true,
            autoSkip: true,
            maxRotation: 0,
            minRotation: 0,
            callback: roundTick,
          },
        },
      },
    };
  }, [
    axisBounds,
    clean,
    peaks,
    primary,
    showFill,
    showPeakLines,
    showQuadrants,
    xTitle,
    yTitle,
  ]);

  return (
    <div className="w-full flex flex-col gap-2 text-left">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="subtitle-text">{title}</h2>

          {subtitle ? (
            <p className="text-sm text-greyscale-3">{subtitle}</p>
          ) : null}
        </div>

        {showControls ? (
          <div className="shrink-0">
            <Switch checked={showFill} onChange={setShowFill} label="Fill" />
          </div>
        ) : null}
      </div>

      {clean.length >= 2 ? (
        <div style={{ width: "100%", height }}>
          <Line data={data} options={options} />
        </div>
      ) : (
        <div className="muted-text text-center py-8">
          No flow-loop data available.
        </div>
      )}
    </div>
  );
}

const FlowLoopChart = memo(FlowLoopChartImpl);

export default FlowLoopChart;
